/**
 * Customer-only dialogs for the RFQ detail page: send to suppliers,
 * cancel, and new query. Kept out of the page so RFQDetail stays readable.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Search } from 'lucide-react';
import type { SupplierOption } from './rfqWizardTypes';

interface SendDialogProps {
  open: boolean;
  busy: boolean;
  /** Suppliers the customer may invite (same source as the create wizard). */
  suppliers: SupplierOption[];
  suppliersLoading: boolean;
  /** Suppliers already on the RFQ (existing responses) — pre-ticked. */
  initialSelected: number[];
  /** 'send' for a draft; 'invite' adds suppliers to an RFQ already sent
   *  (existing ones are shown ticked and locked, only new ones are sent). */
  mode?: 'send' | 'invite';
  onOpenChange: (open: boolean) => void;
  onConfirm: (supplierIds: number[]) => void;
}

/**
 * Send an RFQ from its detail page.
 *
 * Used to send `supplier_ids: []` and rely on the server falling back to the
 * RFQ's existing responses — which a draft does not have. "Create as draft"
 * in the wizard never persists the suppliers picked there either, so every
 * draft sent from this page failed with "At least one supplier must be
 * selected", hidden behind a generic toast. The dialog now lets the customer
 * choose who receives it, exactly like the wizard's Suppliers step.
 */
export function SendToSuppliersDialog({ open, busy, suppliers, suppliersLoading, initialSelected, mode = 'send', onOpenChange, onConfirm }: SendDialogProps) {
  const [selected, setSelected] = useState<number[]>(initialSelected);
  const [search, setSearch] = useState('');

  // Re-seed from the RFQ each time the dialog opens.
  useEffect(() => {
    if (open) { setSelected(initialSelected); setSearch(''); }
  }, [open, initialSelected]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? suppliers.filter((s) => s.company_name.toLowerCase().includes(q)) : suppliers;
  }, [suppliers, search]);

  const locked = mode === 'invite' ? new Set(initialSelected) : new Set<number>();
  const newlySelected = selected.filter((id) => !locked.has(id));
  const toggle = (companyId: number) => {
    if (locked.has(companyId)) return;
    setSelected((prev) => (prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'invite' ? 'Invite More Suppliers' : 'Send RFQ to Suppliers'}</DialogTitle>
          <DialogDescription>
            {mode === 'invite'
              ? 'Suppliers already invited are ticked and cannot be removed here. Newly ticked suppliers are notified and can respond until the deadline.'
              : 'Choose the suppliers to invite. The RFQ moves to "Sent"; each supplier is notified and can begin submitting a response.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search suppliers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              aria-label="Search suppliers"
            />
          </div>

          <div className="max-h-72 overflow-y-auto rounded-md border divide-y" data-testid="send-supplier-list">
            {suppliersLoading ? (
              <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading suppliers…
              </div>
            ) : visible.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground text-center">
                {suppliers.length === 0 ? 'No active suppliers to invite.' : 'No suppliers match your search.'}
              </p>
            ) : visible.map((s) => {
              const checked = selected.includes(s.company);
              const isLocked = locked.has(s.company);
              return (
                <label key={s.company} className={`flex items-center gap-3 px-3 py-2 ${isLocked ? 'opacity-70 cursor-default' : 'cursor-pointer hover:bg-muted/50'}`}>
                  <Checkbox checked={checked} disabled={isLocked} onCheckedChange={() => toggle(s.company)} aria-label={`Invite ${s.company_name}`} />
                  <span className="text-sm">{s.company_name}</span>
                  {isLocked && <span className="ml-auto text-xs text-muted-foreground">Already invited</span>}
                </label>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground">
            {mode === 'invite'
              ? (newlySelected.length === 0
                ? `${locked.size} already invited — tick at least one more supplier.`
                : `${newlySelected.length} more supplier${newlySelected.length === 1 ? '' : 's'} to invite`)
              : (selected.length === 0
                ? 'Select at least one supplier to send.'
                : `${selected.length} supplier${selected.length === 1 ? '' : 's'} selected`)}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={() => onConfirm(mode === 'invite' ? newlySelected : selected)}
            disabled={busy || (mode === 'invite' ? newlySelected.length === 0 : selected.length === 0)}
            className="gap-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'invite'
              ? `Invite ${newlySelected.length > 0 ? newlySelected.length : ''} More`
              : `Send to ${selected.length > 0 ? selected.length : ''} Supplier${selected.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CancelDialogProps {
  open: boolean;
  busy: boolean;
  reason: string;
  onReasonChange: (reason: string) => void;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function CancelRFQDialog({ open, busy, reason, onReasonChange, onOpenChange, onConfirm }: CancelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel RFQ</DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel this RFQ? This action cannot be undone.
            All suppliers will be notified of the cancellation.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <label className="text-sm font-medium">Reason for cancellation (optional)</label>
          <Textarea
            className="mt-2"
            rows={3}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="Enter reason for cancellation..."
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Keep RFQ</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy} className="gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Cancel RFQ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CreateQueryDialogProps {
  open: boolean;
  busy: boolean;
  subject: string;
  message: string;
  /** Selected supplier id, as a string because it comes from a <select>. */
  supplierId: string;
  /** Invited bidders to choose between. Empty for a supplier, who has no choice. */
  supplierOptions: { id: number; name: string }[];
  onSupplierChange: (v: string) => void;
  onSubjectChange: (v: string) => void;
  onMessageChange: (v: string) => void;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function CreateQueryDialog({
  open, busy, subject, message, supplierId, supplierOptions,
  onSupplierChange, onSubjectChange, onMessageChange, onOpenChange, onConfirm,
}: CreateQueryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Query</DialogTitle>
          <DialogDescription>Start a new query thread about this RFQ.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {supplierOptions.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="query-supplier">Supplier</label>
              <select
                id="query-supplier"
                value={supplierId}
                onChange={(e) => onSupplierChange(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">— Select supplier —</option>
                {supplierOptions.map((s) => (
                  <option key={s.id} value={String(s.id)}>{s.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                A thread is private to this supplier — the other bidders never see it.
              </p>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium">Subject</label>
            <Input value={subject} onChange={(e) => onSubjectChange(e.target.value)} placeholder="Enter query subject..." />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Message</label>
            <Textarea value={message} onChange={(e) => onMessageChange(e.target.value)} placeholder="Enter your message..." rows={4} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={onConfirm} disabled={busy} className="gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Create Query
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
